import { AsyncPipe, NgClass } from "@angular/common";
import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    forwardRef,
    Input,
    OnDestroy,
    OnInit,
    ViewChild
} from "@angular/core";
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from "@angular/forms";
import { NgMultiLabelTemplateDirective, NgOptionTemplateDirective, NgSelectComponent } from "@ng-select/ng-select";
import { concat, Observable, of, Subject } from "rxjs";
import {
    catchError,
    debounceTime,
    distinctUntilChanged,
    switchMap,
    takeUntil,
    tap
} from "rxjs/operators";
import { GuidUtils } from "~app/common/utils/guid-utils";
import { RequiredDirective } from "../../directives/required/required.directive";
import { ValidationMessageComponent } from "../../validation/validation-message/validation-message.component";
import { ControlValueAccessorBaseComponent } from "../base-component/control-value-accessor-base.component";
import { InfoComponent } from "../info/info.component";
@Component({
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => MultiSelectorComponent)
        },
        {
            multi: false,
            provide: ControlValueAccessorBaseComponent,
            useExisting: forwardRef(() => MultiSelectorComponent)
        }
    ],
    selector: "rask-multi-selector",
    styleUrls: ["./multi-selector.component.scss"],
    templateUrl: "./multi-selector.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        RequiredDirective,
        InfoComponent,
        NgSelectComponent,
        FormsModule,
        NgMultiLabelTemplateDirective,
        NgOptionTemplateDirective,
        NgClass,
        ValidationMessageComponent,
        AsyncPipe
    ]
})
export class MultiSelectorComponent extends ControlValueAccessorBaseComponent implements AfterViewInit, OnInit, OnDestroy, ControlValueAccessor {
    @Input() selected: any[] = null;
    @Input() items: Observable<any[]>;
    @Input() hideSelected = false;
    @Input() clearAllTooltipText: string;
    @Input() bindLabel?: string;
    @Input() bindValue?: string;
    @Input() groupBy?: string;
    @Input() searchFn = null;
    @Input() typeToSearchText?: string;
    @Input() loadingText?: string;
    @Input() notFoundText?: string;
    @Input() minTermLength?: number;
    @Input() typeaheadFn = null;
    @Input() portalToBody = null;
    @Input() debounceTime = 500;
    @Input() closeOnSelect = false;

    @ViewChild("instance") ngselect: NgSelectComponent;
    loading = false;
    typeaheadInput$ = new Subject<string>();
    isPopupOpen = false;

    readonly targetId = `rask-multi-selector-target-${GuidUtils.newGuid()}`;
    constructor() {
        super();
    }

    @Input() viewFormatter = (item: any) => this.bindLabel ? item[this.bindLabel] : item;
    @Input() viewSelectedFormatter = (item: any) => this.bindLabel ? item[this.bindLabel] : item;
    @Input() itemDisabled = (item: any) => false;

    onSelectionChange(items: any[]) {
        const result = [];
        items.forEach(item => {
            if (this.itemDisabled(item)) {
                return;
            }
            let itemValue = item;
            if (this.bindValue) {
                itemValue = item[this.bindValue];
            }

            result.push(itemValue);
        });

        this.value = result;
    }

    ngOnInit() {
        super.ngOnInit();
        if (this.typeaheadFn) {
            this.loadData();
        }

        this.items.pipe(
            takeUntil(this.destroy$)
        ).subscribe(this.prepareItems);
    }

    onOpen($event) {
        this.isPopupOpen = true;
        this.focus.next(true);
    }

    onClose($event) {
        this.isPopupOpen = false;
        this.focus.next(false);
    }

    prepareItems = (items: any[]) => {
        items?.forEach(item => {
            if (typeof item !== "string") {
                item.disabled = this.itemDisabled(item);
            }
        });
    };

    writeValue(value: any): void {
        super.writeValue(value);
        this.selected = value;
    }

    ngAfterViewInit() {
        super.ngAfterViewInit();
    }

    getLabel(item: any): string {
        return this.viewSelectedFormatter(item);
    }

    loadData() {
        this.items = concat(
            of([]), // default items
            this.typeaheadInput$.pipe(
                debounceTime(this.debounceTime),
                distinctUntilChanged(),
                tap(() => this.loading = true),
                switchMap(term => this.getData(term).pipe(
                    catchError(() => of([])), // empty list on error
                    tap(() => this.loading = false)
                ))
            )
        );
    }

    getData(term: string = null): Observable<any[]> {
        if (!term) {
            return of([]);
        }

        return this.typeaheadFn(term);
    }
}
